import { ExternalLink, Loader2, Plus, Telescope, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, adminHeaders } from "@/lib/api";

// Pesquisa profunda do Infinitum: manual, 12-15 min, 6 por dia partilhadas por
// todos os clientes. O job_id fica guardado neste browser por dossiê, para se
// poder fechar o editor e voltar mais tarde.
const STORAGE_PREFIX = "vo-deep-research:";
const POLL_MS = 15000;
const RUNNING = new Set(["pending", "running", "queued"]);

const STAGE_LABELS = {
        exploring: "a explorar a web",
        verifying: "a verificar as citações",
        analysing: "a procurar lacunas",
        writing: "a escrever o relatório",
};

const SUB_QUESTION_LABELS = {
        answered: "respondida",
        partial: "parcial",
        contradictory: "contraditória",
        no_source: "sem fonte",
};

const readStored = (key) => {
        try {
                const raw = window.localStorage.getItem(key);
                return raw ? JSON.parse(raw) : null;
        } catch {
                return null;
        }
};

const writeStored = (key, value) => {
        try {
                if (value) window.localStorage.setItem(key, JSON.stringify(value));
                else window.localStorage.removeItem(key);
        } catch {
                // Sem armazenamento: a pesquisa continua, só não sobrevive a fechar o editor.
        }
};

const errorDetail = (e, fallback) => {
        const detail = e?.response?.data?.detail;
        return typeof detail === "string" && detail ? detail : fallback;
};

const defaultQuestion = (form) =>
        [form?.title, form?.summary].filter((part) => String(part || "").trim()).join(". ").slice(0, 2000);

export const DeepResearchPanel = ({ form, topicKey, onResult, onAddSources }) => {
        const storageKey = `${STORAGE_PREFIX}${topicKey || "new"}`;
        const [state, setState] = useState(() => readStored(storageKey));
        const [question, setQuestion] = useState("");
        const [busy, setBusy] = useState(false);
        const timer = useRef(null);

        const update = useCallback(
                (next) => {
                        setState(next);
                        writeStored(storageKey, next);
                },
                [storageKey]
        );

        // Ao mudar de dossiê, recupera o que estava guardado para ele.
        useEffect(() => {
                setState(readStored(storageKey));
                setQuestion("");
        }, [storageKey]);

        useEffect(() => {
                onResult?.(state?.status === "succeeded" && state?.use !== false ? state.result : null);
        }, [state, onResult]);

        const poll = useCallback(async () => {
                if (!state?.job_id || !RUNNING.has(state.status)) return;
                try {
                        const { data } = await api.get(`/admin/deep-research/${state.job_id}`, { headers: adminHeaders() });
                        const next = { ...state, status: data.status, progress: data.progress, error: data.error };
                        if (data.status === "succeeded") {
                                next.result = data.result;
                                next.use = true;
                                toast.success("Pesquisa profunda concluída.");
                        } else if (data.status === "failed") {
                                toast.error(`Pesquisa profunda falhou: ${data.error || "sem detalhe"}`);
                        }
                        update(next);
                } catch (e) {
                        if (e?.response?.status === 404) {
                                update({ ...state, status: "failed", error: "O Infinitum já não conhece esta pesquisa." });
                        }
                        // Outras falhas (rede, Infinitum em baixo): tenta-se de novo no próximo ciclo.
                }
        }, [state, update]);

        useEffect(() => {
                if (!state?.job_id || !RUNNING.has(state.status)) return undefined;
                timer.current = window.setTimeout(poll, state.progress ? POLL_MS : 5000);
                return () => window.clearTimeout(timer.current);
        }, [state, poll]);

        const start = async () => {
                const text = (question || defaultQuestion(form)).trim();
                if (text.length < 10) {
                        toast.error("Escreve uma pergunta com pelo menos 10 caracteres.");
                        return;
                }
                if (!window.confirm("A pesquisa profunda demora 12 a 15 minutos e gasta uma das 6 pesquisas diárias do Infinitum. Continuar?")) {
                        return;
                }
                setBusy(true);
                try {
                        const { data } = await api.post(
                                "/admin/deep-research",
                                { question: text, topic_id: topicKey && topicKey !== "new" ? String(topicKey) : undefined },
                                { headers: adminHeaders() }
                        );
                        update({ job_id: data.job_id, question: text, status: "pending", progress: null, max_rounds: data.max_rounds, started_at: new Date().toISOString() });
                        toast.success("Pesquisa profunda lançada. Podes continuar a trabalhar; o resultado aparece aqui.");
                } catch (e) {
                        toast.error(errorDetail(e, "Não foi possível lançar a pesquisa profunda."));
                } finally {
                        setBusy(false);
                }
        };

        const cancel = async () => {
                const beforeExploration = !state?.progress;
                const message = beforeExploration
                        ? "Cancelar a pesquisa? Ainda não começou a explorar, por isso a vaga do dia é devolvida."
                        : "Cancelar a pesquisa? Já começou a explorar: a vaga do dia já não é devolvida.";
                if (!window.confirm(message)) return;
                setBusy(true);
                try {
                        await api.delete(`/admin/deep-research/${state.job_id}`, { headers: adminHeaders() });
                        update(null);
                        toast.success(beforeExploration ? "Pesquisa cancelada; a vaga do dia foi devolvida." : "Pesquisa cancelada.");
                } catch (e) {
                        toast.error(errorDetail(e, "Não foi possível cancelar."));
                } finally {
                        setBusy(false);
                }
        };

        const result = state?.status === "succeeded" ? state.result || {} : null;
        const running = state?.job_id && RUNNING.has(state.status);
        const progress = state?.progress;

        return (
                <div className="mt-6 border-t pt-4" style={{ borderColor: "var(--line)" }} data-testid="deep-research-panel">
                        <div className="flex items-center gap-2">
                                <Telescope size={15} strokeWidth={1.75} />
                                <span className="overline text-[var(--ink-soft)]">Pesquisa profunda (Infinitum)</span>
                        </div>
                        <p className="mt-1 text-xs text-[var(--ink-faint)] max-w-2xl">
                                Para aprofundar um tema já escolhido: várias rondas de pesquisa na web, e só entram fontes cuja citação o Infinitum encontrou na página. Demora 12 a 15 min e há 6 por dia, partilhadas.
                        </p>

                        {!running && !result && (
                                <>
                                        <textarea
                                                value={question}
                                                onChange={(e) => setQuestion(e.target.value)}
                                                placeholder={defaultQuestion(form) || "Pergunta a investigar..."}
                                                rows={2}
                                                maxLength={2000}
                                                className="w-full border bg-transparent p-3 text-sm mt-3 font-mono"
                                                style={{ borderColor: "var(--line)" }}
                                        />
                                        {state?.status === "failed" && (
                                                <p className="mt-2 text-xs text-[var(--deception)]">A última pesquisa falhou: {state.error || "sem detalhe"}</p>
                                        )}
                                        <button type="button" onClick={start} disabled={busy} className="adm-btn adm-btn-outline mt-3">
                                                {busy ? <Loader2 size={14} className="animate-spin" /> : <Telescope size={14} strokeWidth={1.75} />} Lançar pesquisa profunda
                                        </button>
                                </>
                        )}

                        {running && (
                                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                                        <Loader2 size={14} className="animate-spin" />
                                        <span>
                                                {progress
                                                        ? `Ronda ${progress.round || 1} de ${progress.max_rounds || state.max_rounds || 3}, ${STAGE_LABELS[progress.stage] || progress.stage || "a trabalhar"}${progress.verified_sources != null ? ` · ${progress.verified_sources} fonte(s) verificada(s)` : ""}`
                                                        : "À espera de começar (pode aguardar quota do Infinitum)"}
                                        </span>
                                        <button type="button" onClick={cancel} disabled={busy} className="adm-btn adm-btn-outline">
                                                <X size={14} strokeWidth={1.75} /> Cancelar
                                        </button>
                                </div>
                        )}

                        {result && (
                                <div className="mt-3 space-y-4 text-sm">
                                        <p className="text-xs text-[var(--ink-soft)]">Pergunta: {state.question}</p>
                                        <label className="flex items-center gap-2 text-xs">
                                                <input type="checkbox" checked={state.use !== false} onChange={(e) => update({ ...state, use: e.target.checked })} />
                                                Usar as fontes verificadas na próxima geração
                                        </label>

                                        {(result.key_findings || []).length > 0 && (
                                                <div>
                                                        <p className="overline text-[var(--ink-soft)] mb-1">Conclusões</p>
                                                        <ul className="list-disc pl-5 space-y-1">
                                                                {result.key_findings.map((item, i) => (
                                                                        <li key={i}>{item.finding}</li>
                                                                ))}
                                                        </ul>
                                                </div>
                                        )}

                                        <div>
                                                <div className="flex items-center justify-between mb-1">
                                                        <p className="overline text-[var(--ink-soft)]">Fontes verificadas ({(result.verified_sources || []).length})</p>
                                                        {(result.verified_sources || []).length > 0 && (
                                                                <button type="button" onClick={() => onAddSources?.(result.verified_sources)} className="adm-btn adm-btn-outline">
                                                                        <Plus size={14} strokeWidth={1.75} /> Juntar ao dossiê
                                                                </button>
                                                        )}
                                                </div>
                                                <ul className="space-y-2">
                                                        {(result.verified_sources || []).map((source) => (
                                                                <li key={source.url} className="border-l-2 pl-3" style={{ borderColor: "var(--truth)" }}>
                                                                        <a href={source.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                                                                                {source.title} <ExternalLink size={12} />
                                                                        </a>
                                                                        <span className="ml-2 text-xs text-[var(--ink-faint)]">
                                                                                {[source.outlet, source.published_at?.slice(0, 10)].filter(Boolean).join(" · ")}
                                                                        </span>
                                                                        {(source.usable_quotes || []).slice(0, 2).map((quote, i) => (
                                                                                <p key={i} className="text-xs italic text-[var(--ink-soft)]">“{quote}”</p>
                                                                        ))}
                                                                </li>
                                                        ))}
                                                </ul>
                                        </div>

                                        {(result.unconfirmed_leads || []).length > 0 && (
                                                <details>
                                                        <summary className="cursor-pointer overline text-[var(--ink-soft)]">
                                                                Pistas por confirmar ({result.unconfirmed_leads.length}) — não são fontes
                                                        </summary>
                                                        <ul className="mt-2 space-y-1 text-xs">
                                                                {result.unconfirmed_leads.map((lead) => (
                                                                        <li key={lead.url}>
                                                                                <a href={lead.url} target="_blank" rel="noreferrer" className="underline">{lead.title || lead.url}</a>
                                                                                <span className="ml-2 text-[var(--ink-faint)]">{lead.reason}</span>
                                                                        </li>
                                                                ))}
                                                        </ul>
                                                </details>
                                        )}

                                        {(result.sub_questions || []).length > 0 && (
                                                <details>
                                                        <summary className="cursor-pointer overline text-[var(--ink-soft)]">Subperguntas</summary>
                                                        <ul className="mt-2 space-y-1 text-xs">
                                                                {result.sub_questions.map((item, i) => (
                                                                        <li key={i}>
                                                                                <strong>{SUB_QUESTION_LABELS[item.status] || item.status}:</strong> {item.question}
                                                                        </li>
                                                                ))}
                                                        </ul>
                                                </details>
                                        )}

                                        {result.report_markdown && (
                                                <details>
                                                        <summary className="cursor-pointer overline text-[var(--ink-soft)]">Relatório completo</summary>
                                                        <div className="mt-2 whitespace-pre-wrap text-xs font-mono">{result.report_markdown}</div>
                                                </details>
                                        )}

                                        <button type="button" onClick={() => update(null)} className="adm-btn adm-btn-outline">
                                                Descartar e fazer nova pesquisa
                                        </button>
                                </div>
                        )}
                </div>
        );
};
